import Button, { BUTTON_VARIANTS } from '@/atoms/Button';
import Spinner from '@/atoms/loader/Spinner';
import CardThumb from '@/molecules/CardThumb';
import DropDown from '@/molecules/DropDown';
import PageLeading from '@/molecules/PageLeading';
import TabsLink from '@/molecules/Tabs/TabsLink';
import CardCTATools from '@/organisms/CardCTATools';
import ListOffer from '@/organisms/ListOffer';
import cardDetailsDto from 'hooks/queries/cards/useCardsDetailsApi/dto';
import useUserApi from 'hooks/UserApi/useUserApi';
import { useTranslation } from 'react-i18next';
import { TemplateType } from 'types/cms';
import { ALIGNMENT_ENUM, SIZE } from 'types/utils';
import { useCmsData } from 'utils/cms';
import cardsFactory from 'utils/factories/card';
import CollectionTab from './components/CollectionTab';
import LoreTab from './components/LoreTab';
import useCardDetails from './hooks';

enum TABS_SLUG {
  COLLECTION = '',
  LORE = 'lore',
  OFFERS = 'offers',
}

const CardDetails: TemplateType = () => {
  const { card, currTab, tabs, lore, languagesItems, currentLang } = useCardDetails();
  const { generalSettings } = useCmsData();
  const { user } = useUserApi();
  const { t } = useTranslation();
  if (!card) {
    return <Spinner size={SIZE.LG} />;
  }

  return (
    <>
      <PageLeading alignment={ALIGNMENT_ENUM.center} title={card.name}>
        {user && card.reference && <CardCTATools reference={card.reference} />}
      </PageLeading>
      <div className="grid-12 gap-4 lg:gap-8 items-start">
        <div className="col-span-full md:col-span-4 flex flex-col items-center gap-4 w-fit">
          <CardThumb
            alt=""
            foiled={card.stats?.foiled}
            imagePath={card.allImagePath?.[currentLang]}
            inMyCollection={card.stats?.inMyCollection}
            inMySale={card.stats?.inMySale}
            isExclusive={!!card.stats?.isExclusive}
            isSuspended={card.isSuspended}
            numberCopyAvailable={card.stats?.numberCopyAvailable}
            rarity={card.rarity?.reference}
          />
          {!!generalSettings.cardLanguageSwitch && (
            <DropDown
              align="start"
              items={languagesItems}
              trigger={
                <Button
                  className="flex-row-reverse group justify-between w-full"
                  icon="fa-solid fa-caret-left group-data-state-open:rotate-90 -rotate-90"
                  variant={BUTTON_VARIANTS.SECONDARY}
                >
                  {t(currentLang)}
                </Button>
              }
            />
          )}
        </div>

        <div className="col-span-full md:col-span-8 flex flex-col gap-4  w-full md:w-auto">
          <TabsLink className="mb-2" items={tabs} />
          {currTab === TABS_SLUG.COLLECTION && <CollectionTab card={card} />}
          {currTab === TABS_SLUG.LORE && lore && card.reference && <LoreTab reference={card.reference} />}
          {currTab === TABS_SLUG.OFFERS && card.reference && (
            <ListOffer cardRef={card.reference} itemsPerPage={10} withPagination />
          )}
        </div>
      </div>
    </>
  );
};

CardDetails.getServerSideFunction =
  async (_data, apiClient) =>
  ({ queryClient, query, locale }) => {
    const reference = query?.slug && query?.slug[1];

    return queryClient.prefetchQuery({
      queryKey: cardsFactory.details(reference || ''),
      queryFn: async () => {
        const res = await apiClient.cards.apiCardsReferenceGet(reference || '', { locale: locale! });

        const t = cardDetailsDto(res.data);

        return t;
      },
    });
  };
export default CardDetails;
